import {
  interaction,
  SizableText,
  styled,
  XStack,
  YStack,
  YStackProps,
} from '@mythical/ui'

import { memo } from 'react'
import ContentThumbnail, { ContentThumbnailProps } from '../ContentThumbnail'
import { differenceInDays } from 'date-fns'
import { SmartLink } from 'app/components/primitive/SmartLink'
import { useStores } from 'app/store'
import { mapNameToTier } from 'app/constants/tiers'
import { ColorEnum } from 'app/utils/mapColors'

const CardWrapper = styled(YStack, {
  fs: 1,
  fg: 1,
  gap: '$2',
  fd: 'column',
  jc: 'flex-start',
  ai: 'flex-start',
  width: '100%',
})

const CardContent = styled(YStack, {
  gap: '$1.5',
  width: '100%',
  fs: 1,
  fg: 1,
  fd: 'column',
  jc: 'flex-start',
  ai: 'flex-start',
  $gtSm: {
    gap: '$2',
  },
})

const CardHeading = styled(SizableText, {
  color: '$fgPrimary',
  ff: '$body',
  size: '$1',
  fow: '500',
  $gtSm: {
    fow: '500',
    size: '$2',
  },
})

const CardText = styled(SizableText, {
  color: '$fgSecondary',
  ff: '$body',
  size: '$0.75',
  $gtSm: {
    size: '$1',
  },
})

export interface ContentCardProps {
  id: string
  image: string
  title: string
  datePublished: string
  duration?: string
  category: string
  href?: string
  slug: string
  categoryHref?: string
  wrapperProps?: YStackProps
  thumbnailProps?: ContentThumbnailProps['wrapperProps']
  videoPost?: boolean
  secondsWatched?: number
  contentTier?: number
  onNavigate?: () => void
  children?: React.ReactNode
  tag?: string
  hideLock?: boolean
  sizes?: string

  // TODO: Does not make sense to be here, maybe have a generic content card props interface?
  commentCount: number
  image2x: string
  disableComments?: boolean
  hideInfo?: boolean
  backgroundColour?: ColorEnum | null
  foregroundColour?: ColorEnum | null
  seriesOverlayUrl?: string
}

const ContentCardComponent = ({
  image,
  title,
  duration,
  category,
  href,
  thumbnailProps,
  wrapperProps,
  videoPost = false,
  secondsWatched,
  onNavigate,
  datePublished,
  contentTier,
  children,
  tag,
  hideLock = false,
  seriesOverlayUrl,
  sizes = '(min-width: 768px) 350px, 170px',
}: ContentCardProps) => {
  const { userStore } = useStores()
  const isLocked =
    !!userStore?.user?.tier &&
    !!contentTier &&
    Number(mapNameToTier(userStore?.user?.tier)) < contentTier

  const isNew = differenceInDays(new Date(), new Date(datePublished)) < 7

  return (
    <CardWrapper {...wrapperProps}>
      <YStack width="100%" {...thumbnailProps}>
        <ContentThumbnail
          href={href}
          image={image}
          duration={duration}
          secondsWatched={secondsWatched}
          contentTier={contentTier}
          isLocked={!hideLock && isLocked}
          onPress={onNavigate}
          seriesOverlayUrl={seriesOverlayUrl}
          sizes={sizes}
          {...(isNew && { tag: 'New' })}
          {...(tag && { tag: tag })}
        />
      </YStack>
      <XStack jc="space-between" ai="flex-start" gap="$2" w="100%">
        <SmartLink
          Component={CardContent}
          href={href}
          onPress={onNavigate}
          {...interaction.opacity}
        >
          {category?.length > 1 && <CardText>{category}</CardText>}
          <CardHeading>{title}</CardHeading>
        </SmartLink>
        {children}
      </XStack>
    </CardWrapper>
  )
}

const ContentCard = memo(ContentCardComponent)

export default ContentCard
